import axios, { InternalAxiosRequestConfig, AxiosError } from "axios";
import { IPublicClientApplication } from "@azure/msal-browser";
import { isTokenExpiredOrExpiringSoon, refreshAccessToken } from "../../utils/tokenRefresh";
import { COMMON } from "../../constants/common";
import { RouterPathEnum } from "../../constants/router";

// Store the MSAL instance and navigation function
let msalInstance: IPublicClientApplication | null = null;
let navigateFunction: ((path: string) => void) | null = null;
let updateAccessTokenFunction: ((token: string | null) => void) | null = null;
let isInterceptorInitialized = false;

/**
 * Initialize the axios interceptor with required dependencies
 * This should be called once during app initialization
 */
export const initializeAxiosInterceptor = (
  instance: IPublicClientApplication,
  navigate: (path: string) => void,
  updateAccessToken: (token: string | null) => void
) => {
  // Prevent multiple initializations
  if (isInterceptorInitialized) {
    console.warn("Axios interceptor already initialized. Skipping...");
    return;
  }

  msalInstance = instance;
  navigateFunction = navigate;
  updateAccessTokenFunction = updateAccessToken;
  isInterceptorInitialized = true;

  // Request interceptor to refresh token before API calls
  axios.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Only process requests that require authentication (have Authorization header)
      const authHeader = config.headers?.Authorization;
      if (!authHeader || typeof authHeader !== "string" || !authHeader.startsWith("Bearer ")) {
        return config;
      }

      // Get current token from localStorage
      const currentToken = localStorage.getItem(COMMON.ACCESS_TOKEN);

      // Check if token needs refresh
      if (currentToken && isTokenExpiredOrExpiringSoon(currentToken)) {
        try {
          if (!msalInstance) {
            throw new Error("MSAL instance not initialized");
          }

          // Get the current account from MSAL
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length === 0) {
            throw new Error("No authenticated account found");
          }

          // Refresh the token
          const newToken = await refreshAccessToken(msalInstance, accounts[0]);

          // Update the token in the context
          if (updateAccessTokenFunction) {
            updateAccessTokenFunction(newToken);
          }

          // Update the Authorization header with the new token
          config.headers.Authorization = `Bearer ${newToken}`;
        } catch (error: any) {
          console.error("Failed to refresh token:", error);

          // Clear token from storage
          localStorage.removeItem(COMMON.ACCESS_TOKEN);
          if (updateAccessTokenFunction) {
            updateAccessTokenFunction(null);
          }

          // Redirect to login page
          if (navigateFunction) {
            navigateFunction(`/${RouterPathEnum.LOGIN}`);
          } else {
            // Fallback: use window.location if navigate is not available
            window.location.href = `/${RouterPathEnum.LOGIN}`;
          }

          // Cancel the request
          return Promise.reject(new Error("Token refresh failed. Redirecting to login."));
        }
      } else if (currentToken) {
        // Token is still valid, ensure it's in the header
        config.headers.Authorization = `Bearer ${currentToken}`;
      }

      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle 401 errors (token expired on server side)
  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // If we get a 401, try to refresh the token once
      if (error.response?.status === 401 && msalInstance) {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Prevent infinite retry loop
        if (originalRequest._retry) {
          // Already retried, redirect to login
          localStorage.removeItem(COMMON.ACCESS_TOKEN);
          if (updateAccessTokenFunction) {
            updateAccessTokenFunction(null);
          }
          if (navigateFunction) {
            navigateFunction(`/${RouterPathEnum.LOGIN}`);
          } else {
            window.location.href = `/${RouterPathEnum.LOGIN}`;
          }
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length === 0) {
            throw new Error("No authenticated account found");
          }

          // Try to refresh the token
          const newToken = await refreshAccessToken(msalInstance, accounts[0]);

          // Update token in context
          if (updateAccessTokenFunction) {
            updateAccessTokenFunction(newToken);
          }

          // Retry the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }

          return axios(originalRequest);
        } catch (refreshError) {
          console.error("Token refresh failed on 401:", refreshError);

          // Clear token and redirect to login
          localStorage.removeItem(COMMON.ACCESS_TOKEN);
          if (updateAccessTokenFunction) {
            updateAccessTokenFunction(null);
          }
          if (navigateFunction) {
            navigateFunction(`/${RouterPathEnum.LOGIN}`);
          } else {
            window.location.href = `/${RouterPathEnum.LOGIN}`;
          }

          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};
